import { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { CustomButton, CustomTextInput, FullScreenLoader } from "src/app/components";
import ProfileLayout from "src/app/layouts/ProfileLayout";
import { AppRoutes } from "src/app/navigation/routes";
import { useAppDispatch, useAppSelector } from "src/app/services/store";
import { APP_MESSAGES } from "src/shared/config/src/lib/constants";
import { getCurrentChild } from "src/shared/redux-state/src";
import { fetchCommunications } from "src/shared/redux-state/src/lib/messages/communication/CommunicationHandler";
import { sendCommunicationMessage } from "src/shared/services/src";
import { ChildrenStoreData, CommunicationReplyPostData, CommunicationReplyResponseType, ErrorType } from "src/shared/types/src";
import { toast } from "react-toastify";
import { checkIsEylogOnlyNursery } from "src/app/utilities/helpers";

export default function CommunicationReply() {

    const [msg, setMmsg] = useState<string>('');
    const [errors, setErrors] = useState<ErrorType>({});


  const buttonRef: any = useRef<HTMLButtonElement>(null);
    const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
    const [isShowingErrorMsg, setIsShowingErrorMsg] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState(APP_MESSAGES.generalErrorMessage);

    const navigate = useNavigate();
    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
    const dispatch = useAppDispatch()

    const { communicationId } = useParams();

    useEffect(() => {
        if (checkIsEylogOnlyNursery(currentChild)) {
            navigate(AppRoutes.EYLOG_MESSAGES);
        };
    }, [currentChild.childInstanceKey]);

    const FIELD_MAP = {
        MESSAGE: 'message',
    };

    const runValidators = () => {
        let errorObj: ErrorType = {};
        setErrors(Object.create({}));

        if (!msg || msg.trim().length === 0) {
            errorObj = {
                ...errorObj,
                [FIELD_MAP.MESSAGE]: {
                    isErrored: true,
                    errorMsg: 'Message is required',
                },
            };
        }

        setErrors(errorObj);
        return errorObj;
    };

    const onSend = async () => {
        try {
            const validity = runValidators();

            if (validity && Object.values(validity).some(({ isErrored }) => isErrored)) {
                return;
            }

            setIsSubmitLoading(true);
            const postData: CommunicationReplyPostData = {
                message: msg,
                attachments: []
            };

            const response = await sendCommunicationMessage(currentChild.instanceUrl, currentChild.nursery.eymanNurseryId, Number(communicationId), postData)
            const messageResponse = response as CommunicationReplyResponseType;
            if (messageResponse && messageResponse.status) {
                navigate(AppRoutes.COMMUNICATIONS, { state: { refresh: true }});
            } else {
                setIsShowingErrorMsg(true)
                setErrorMessage(APP_MESSAGES.generalErrorMessage);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                const { errors } = error?.response?.data;
                if (errors && Object.keys(errors).length > 0) {
                    setErrorMessage(errors.length > 0 ? errors.join(',') : errors[0]);
                } else {
                    setErrorMessage(APP_MESSAGES.generalErrorMessage);
                }
                setIsShowingErrorMsg(true);
            } else {
                setErrorMessage(APP_MESSAGES.generalErrorMessage);
                setIsShowingErrorMsg(true);
            }
        } finally {
            setIsSubmitLoading(false);
        }
    }

    useEffect(() => {
        if(isShowingErrorMsg) {
            toast.error(errorMessage, {
                onClose: () => setIsShowingErrorMsg(false)
            });
        }
    }, [isShowingErrorMsg, errorMessage]);

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter' && buttonRef.current) {
          buttonRef.current.click();
        }
      };

    return (
        <ProfileLayout title={"Reply to communication"} fixedFooter={true} showProfileImage={false}>
            {isSubmitLoading ? <FullScreenLoader /> : null}
            <div>
                <CustomTextInput label={'Message'} asterisk={true} multiline={true}
                    type="message"
                    name="message"
                    value={msg}
                    onChange={(e) => setMmsg(e.target.value)}
                    maxRows={20}
                    handleKeyDown={handleKeyDown}
                />
                {
                    errors?.[FIELD_MAP.MESSAGE]?.errorMsg
                    && errors?.[FIELD_MAP.MESSAGE]?.isErrored
                    && <span className="form-error">{errors[FIELD_MAP.MESSAGE].errorMsg}</span>
                }
                <div className="section-fixed pd-b-10 pd-x-20">
                    <CustomButton color="primary" onClick={onSend} text="Send" buttonRef={buttonRef} />
                </div>
            </div>
        </ProfileLayout>
    );
}
